import React, { createContext, useContext, useState, useEffect } from 'react';
import { checklistTemplates } from '../data/checklistTemplates';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedProjects = localStorage.getItem('gospelwise-projects');
    const savedChecklistItems = localStorage.getItem('gospelwise-checklist-items');
    
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    }
    
    if (savedChecklistItems) {
      setChecklistItems(JSON.parse(savedChecklistItems));
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('gospelwise-projects', JSON.stringify(projects));
  }, [projects]);

  useEffect(() => {
    localStorage.setItem('gospelwise-checklist-items', JSON.stringify(checklistItems));
  }, [checklistItems]);

  const addProject = (projectData) => {
    const newProject = {
      ...projectData,
      id: Date.now().toString(),
      status: 'Drafting',
      progress: 0,
      created_at: new Date().toISOString()
    };
    
    setProjects(prev => [...prev, newProject]);
    
    // Create checklist items for this project
    const newChecklistItems = checklistTemplates.map(template => ({
      id: `${newProject.id}-${template.id}`,
      project_id: newProject.id,
      tab: template.tab,
      label: template.label,
      description: template.description,
      completed: false
    }));
    
    setChecklistItems(prev => [...prev, ...newChecklistItems]);
    
    return newProject.id;
  };

  const updateProject = (id, updates) => {
    setProjects(prev => prev.map(project => 
      project.id === id ? { ...project, ...updates } : project
    ));
  };

  const deleteProject = (id) => {
    setProjects(prev => prev.filter(project => project.id !== id));
    setChecklistItems(prev => prev.filter(item => item.project_id !== id));
  };

  const getProject = (id) => {
    return projects.find(project => project.id === id);
  };

  const getChecklistItems = (projectId, tab = null) => {
    let items = checklistItems.filter(item => item.project_id === projectId);
    if (tab) {
      items = items.filter(item => item.tab === tab);
    }
    return items;
  };

  const updateChecklistItem = (itemId, updates) => {
    setChecklistItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
    
    // Update project progress
    const item = checklistItems.find(i => i.id === itemId);
    if (item) {
      updateProjectProgress(item.project_id);
    }
  };

  const updateProjectProgress = (projectId) => {
    const projectItems = checklistItems.filter(item => item.project_id === projectId);
    const completedItems = projectItems.filter(item => item.completed);
    const progress = projectItems.length > 0 ? Math.round((completedItems.length / projectItems.length) * 100) : 0;
    
    updateProject(projectId, { progress });
  };

  const markProjectAsPublished = (projectId) => {
    updateProject(projectId, { status: 'Published' });
  };

  const value = {
    projects,
    checklistItems,
    addProject,
    updateProject,
    deleteProject,
    getProject,
    getChecklistItems,
    updateChecklistItem,
    markProjectAsPublished
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};